import React, { useState, useEffect, useRef } from "react";

import { Formik, Form } from "formik";
import { Info } from "icons";
import * as yup from "yup";

import dsdeApi from "apis/dsde";
import dsdesExploreApi from "apis/dsdesExplore";
import usersApi from "apis/users";
import {
  Button,
  Pane,
  Typography,
  Table,
  Steps,
  Toastr,
  Pagination,
} from "clearsense-ui/components";
import { Input, Select, Textarea } from "clearsense-ui/components/formik";

import {
  UNIQUE_VALUES_COLUMN,
  SCOPE_TYPES,
  REFERENCE_TYPES,
} from "./constants";

import {
  CREATE_SOURCE_INITIAL_VALUES,
  CREATE_SOURCE_VALIDATION_SCHEMA,
} from "../../../constants/createReferenceSource";

const ProfiledListOfUniqueValues = ({ dsde }) => {
  const [uniqueValues, setUniqueValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per: 20 });

  const setPage = page => {
    setPagination(prev => ({ ...prev, page }));
  };

  const setPer = per => {
    setPagination(prev => ({ ...prev, per }));
  };

  const getUniqueValues = async () => {
    try {
      setIsLoading(true);
      const { data } = await dsdesExploreApi.getUniqueValuesOfDsde(
        dsde?.id,
        pagination?.page,
        pagination?.per
      );
      setUniqueValues(data);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUniqueValues();
  }, [pagination?.page, pagination?.per]);

  return (
    <>
      <Table
        columnData={UNIQUE_VALUES_COLUMN}
        rowData={uniqueValues?.unique_values}
        isLoading={isLoading}
      />
      <Pagination
        pageSize={pagination.per}
        setPageSize={setPer}
        currentPage={pagination.page}
        setCurrentPage={setPage}
        totalRecordsCount={uniqueValues?.total_unique_count?.[0]}
      />
      <div className="flex items-start gap-1.5 mt-5">
        <Info size={20} color="#98A2B3" />
        <Typography style="condensedMedium" className="text-gray-500">
          Only values are available for unique values from a DSDE. To add
          descriptions, start date, end date you can do so from the reference
          detail page.
        </Typography>
      </div>
    </>
  );
};

const CreateFromUniqueValues = ({
  isPaneOpen,
  setIsPaneOpen,
  dataSource,
  cde,
  fetchAssociatedReferenceSource,
}) => {
  const [step, setStep] = useState(1);
  const [referenceSource, setReferenceSource] = useState({});
  const [dsde, setDsde] = useState({});
  const [stewards, setStewards] = useState([]);

  const formRef = useRef();

  const fetchStewards = async () => {
    try {
      const { data } = await usersApi.fetch(1, 1000, "");
      setStewards(
        data?.users?.map(user => ({ label: user.name, value: user.key }))
      );
    } catch (error) {
      logger.log(error);
    }
  };

  const resetForm = () => {
    setStep(1);
    setReferenceSource({});
    formRef.current.resetForm();
    setIsPaneOpen(false);
  };

  const handleSubmit = values => {
    const payload = {
      name: values?.referenceSourceName,
      description: values?.referenceSourceDescription,
      reference_source_type: values?.referenceType?.value,
      reference_source_scope: values?.scope?.value,
      drafter_id: values?.drafter?.value,
      approver_id: values?.approver?.value,
    };
    setReferenceSource(payload);
    setStep(2);
  };

  const createReferenceSource = async () => {
    try {
      const { data } = await dsdesExploreApi.createReferenceSources(dsde?.id, {
        ...referenceSource,
        source_type: "unique_values",
      });
      await dsdesExploreApi.createFromUniqueValues(
        dsde?.id,
        data?.reference_source?.id
      );
      fetchAssociatedReferenceSource();
      resetForm();
    } catch (error) {
      logger.log(error);
    }
  };

  const getDsde = async () => {
    try {
      const dsdeResponse = await dsdeApi.show({
        cde_definition_id: cde?.id,
        data_source_id: dataSource?.id,
      });
      if (dsdeResponse.status === 204) {
        Toastr.info("Please associate a DSDE before continuing.");
        resetForm();
        return "";
      }
      setDsde(dsdeResponse?.data?.dsde);
    } catch (error) {
      logger.log(error);
    }
    return "";
  };

  useEffect(() => {
    isPaneOpen && getDsde();
    setStep(1);
  }, [isPaneOpen]);

  useEffect(() => {
    fetchStewards();
  }, []);

  return (
    <Pane
      isOpen={isPaneOpen}
      onClose={() => setIsPaneOpen(false)}
      size={step === 1 ? "sm" : "lg"}
    >
      <Pane.Header>
        <Typography style="h3" type="bold" className="-mt-3">
          {step === 1
            ? "Create Reference Source From DSDE"
            : "Profiled List of Unique Values from DSDE"}
        </Typography>
        <Typography style="large">
          {step === 1
            ? "Create a reference source with uniques values of the DSDE"
            : "View list of all unique values from DSDE"}
        </Typography>
      </Pane.Header>
      <Pane.Body className="py-8 overflow-auto">
        <Steps
          steps={["Create Source", "Verify Unique Values"]}
          currentStep={step}
          setCurrentStep={setStep}
        />
        {step === 1 ? (
          <Formik
            initialValues={CREATE_SOURCE_INITIAL_VALUES}
            onSubmit={handleSubmit}
            innerRef={formRef}
            validationSchema={yup.object(CREATE_SOURCE_VALIDATION_SCHEMA)}
          >
            <Form className="flex flex-col w-full gap-4 mt-5">
              <Input
                label="Reference Source Name"
                placeholder="Enter Name for the Reference Source"
                name="referenceSourceName"
                required
              />
              <div className="flex items-center gap-1.5">
                <Info size={14} color="#98A2B3" />
                <Typography style="condensedMedium" className="text-gray-500">
                  Default Name: "DataSource_TableName_ColumnName"
                </Typography>
              </div>
              <Textarea
                label="Reference Source Description"
                name="referenceSourceDescription"
                placeholder="Enter a description"
              />
              <Select
                label="Reference Type"
                placeholder="Select Reference Type"
                name="referenceType"
                options={REFERENCE_TYPES}
                required
                className="w-72"
              />
              <Select
                label="Scope (Public/Private)"
                placeholder="Select Scope"
                name="scope"
                options={SCOPE_TYPES}
                required
                className="w-72"
              />
              <Select
                label="Drafter"
                placeholder="Select Steward"
                name="drafter"
                options={stewards}
                required
                className="w-72"
              />
              <Select
                label="Approver"
                placeholder="Select Steward"
                name="approver"
                options={stewards}
                required
                className="w-72"
              />
            </Form>
          </Formik>
        ) : (
          <ProfiledListOfUniqueValues dsde={dsde} />
        )}
      </Pane.Body>
      <Pane.Footer className="flex">
        {step === 1 ? (
          <div className="flex justify-between w-full">
            <Button label="Cancel" style="secondary" onClick={resetForm} />
            <Button
              label="Next"
              style="primary"
              onClick={() => formRef.current.submitForm()}
            />
          </div>
        ) : (
          <>
            <Button label="Save and Close" onClick={createReferenceSource} />
            <Button
              label="Cancel"
              style="secondary"
              onClick={() => setIsPaneOpen(false)}
            />
          </>
        )}
      </Pane.Footer>
    </Pane>
  );
};

export default CreateFromUniqueValues;
